import moment from 'moment';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import getDateTimeFormatMapForLang from '@rover/utilities/datetime';

import buildLastMinuteRequestTitle, { isSupportedServiceType } from './buildLastMinuteRequestTitle';
import { HeaderIconEnum, HeaderTitlesEnum, LastMinuteHeaderTitle } from './constants';
import { BaseHeaderProps } from './types';
import { daysFromToday, isSameDay } from './utils';

export type BuildLastMinuteSearchHeaderParams = {
  startDate?: Date;
  endDate?: Date;
  serviceType?: ServiceType;
  isRecurring: boolean;
  language: string;
  unitCount?: number;
};

const getScenario = (leadTimeDays: number): LastMinuteHeaderTitle | null => {
  switch (leadTimeDays) {
    case 0:
      return HeaderTitlesEnum.LAST_MINUTE_SAME_DAY;
    case 1:
      return HeaderTitlesEnum.LAST_MINUTE_NEXT_DAY;
    case 2:
      return HeaderTitlesEnum.LAST_MINUTE_TWO_DAY;
    default:
      return null;
  }
};

/**
 * Computes the last-minute header from the search start date and request context.
 * Returns null when the lead time is outside the last-minute window (>2 days or
 * past), or when the service type is missing or one this header doesn't support.
 */
const buildLastMinuteSearchHeader = ({
  startDate,
  endDate,
  serviceType,
  isRecurring,
  language,
  unitCount = 1,
}: BuildLastMinuteSearchHeaderParams): Pick<BaseHeaderProps, 'icon' | 'title'> | null => {
  if (!startDate || !moment(startDate).isValid()) {
    return null;
  }

  if (!isSupportedServiceType(serviceType)) {
    return null;
  }

  const leadTimeDays = daysFromToday(startDate);

  const scenario = getScenario(leadTimeDays);
  if (!scenario) {
    return null;
  }

  const isSingleDay = !isRecurring && (!endDate || isSameDay(startDate, endDate));

  const dayOfWeek =
    scenario === HeaderTitlesEnum.LAST_MINUTE_TWO_DAY
      ? moment(startDate)
          .locale(language)
          .format(getDateTimeFormatMapForLang(language).WEEKDAY_FULL)
      : undefined;

  return {
    icon: HeaderIconEnum.TIME,
    title: buildLastMinuteRequestTitle({
      scenario,
      serviceType,
      isSingleDay,
      unitCount,
      dayOfWeek,
    }),
  };
};

export default buildLastMinuteSearchHeader;
